import React, { useState, useEffect } from 'react';
import {
  BellIcon,
  CheckCircleIcon,
  TruckIcon,
  CurrencyDollarIcon,
  MegaphoneIcon,
  ShieldCheckIcon
} from '@heroicons/react/24/outline';

const Notifications: React.FC = () => {
  const [notifications, setNotifications] = useState<any[]>([]);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    // TODO: Integrate with real notifications API endpoint
    // For now, show empty state - no fake/mock data
    setNotifications([]);
  }, []);

  const filteredNotifications = filter === 'all' 
    ? notifications 
    : filter === 'unread'
    ? notifications.filter(n => !n.read)
    : notifications.filter(n => n.type === filter);

  const markAsRead = (id: number) => {
    setNotifications(notifications.map(n => 
      n.id === id ? { ...n, read: true } : n
    ));
  };

  const markAllAsRead = () => {
    setNotifications(notifications.map(n => ({ ...n, read: true })));
  };

  const getColorClasses = (color: string, read: boolean) => {
    const opacity = read ? '50' : '100';
    const bgOpacity = read ? '25' : '100';
    
    switch (color) {
      case 'green':
        return {
          bg: `bg-green-${bgOpacity}`,
          text: `text-green-${opacity === '50' ? '400' : '600'}`,
          border: `border-green-${opacity === '50' ? '100' : '200'}`
        };
      case 'blue':
        return {
          bg: `bg-volexx-blue-${bgOpacity}`,
          text: `text-volexx-blue-${opacity === '50' ? '400' : '600'}`,
          border: `border-volexx-blue-${opacity === '50' ? '100' : '200'}`
        };
      case 'orange':
        return {
          bg: `bg-volexx-orange-${bgOpacity}`,
          text: `text-volexx-orange-${opacity === '50' ? '400' : '600'}`,
          border: `border-volexx-orange-${opacity === '50' ? '100' : '200'}`
        };
      default:
        return {
          bg: `bg-gray-${bgOpacity}`,
          text: `text-gray-${opacity === '50' ? '400' : '600'}`,
          border: `border-gray-${opacity === '50' ? '100' : '200'}`
        };
    }
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  return (
    <div className="min-h-screen bg-gray-50 py-6">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <BellIcon className="h-8 w-8 text-gray-900 mr-3" />
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Notifications</h1>
                {unreadCount > 0 && (
                  <p className="text-sm text-gray-600 mt-1">
                    You have {unreadCount} unread notification{unreadCount !== 1 ? 's' : ''}
                  </p>
                )}
              </div>
            </div>
            {unreadCount > 0 && (
              <button
                onClick={markAllAsRead}
                className="text-sm font-semibold text-volexx-blue-600 hover:text-volexx-blue-700"
              >
                Mark all as read
              </button>
            )}
          </div>
        </div>

        {/* Filters */}
        <div className="mb-6 flex flex-wrap gap-2">
          <button
            onClick={() => setFilter('all')}
            className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${
              filter === 'all'
                ? 'bg-volexx-blue-500 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-100'
            }`}
          >
            All
          </button>
          <button
            onClick={() => setFilter('unread')}
            className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${
              filter === 'unread'
                ? 'bg-volexx-blue-500 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-100'
            }`}
          >
            Unread {unreadCount > 0 && `(${unreadCount})`}
          </button>
          <button
            onClick={() => setFilter('order_status')}
            className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${
              filter === 'order_status'
                ? 'bg-volexx-blue-500 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-100'
            }`}
          >
            Orders
          </button>
          <button
            onClick={() => setFilter('wallet')}
            className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${
              filter === 'wallet'
                ? 'bg-volexx-blue-500 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-100'
            }`}
          >
            Wallet
          </button>
          <button
            onClick={() => setFilter('announcement')}
            className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${
              filter === 'announcement'
                ? 'bg-volexx-blue-500 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-100'
            }`}
          >
            Announcements
          </button>
        </div>

        {/* Notifications List */}
        <div className="space-y-3">
          {filteredNotifications.length === 0 ? (
            <div className="bg-white rounded-volexx-lg shadow-volexx-md p-12 text-center">
              <BellIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">You're all caught up!</h3>
              <p className="text-gray-500">You'll receive notifications about your orders, deliveries, and account activity here.</p>
            </div>
          ) : (
            filteredNotifications.map((notification) => {
              const Icon = notification.icon;
              const colors = getColorClasses(notification.color, notification.read);
              
              return (
                <div
                  key={notification.id}
                  onClick={() => markAsRead(notification.id)}
                  className={`bg-white rounded-volexx-lg shadow-volexx-md p-4 cursor-pointer transition-all hover:shadow-volexx-lg ${
                    !notification.read ? 'border-l-4 border-volexx-blue-500' : ''
                  }`}
                >
                  <div className="flex items-start space-x-4">
                    <div className={`p-2 rounded-volexx ${colors.bg} flex-shrink-0`}>
                      <Icon className={`h-6 w-6 ${colors.text}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between">
                        <h3 className={`text-sm font-semibold ${notification.read ? 'text-gray-600' : 'text-gray-900'}`}>
                          {notification.title}
                        </h3>
                        <span className="text-xs text-gray-500 whitespace-nowrap ml-2">
                          {formatTimestamp(notification.timestamp)}
                        </span>
                      </div>
                      <p className={`text-sm mt-1 ${notification.read ? 'text-gray-500' : 'text-gray-700'}`}>
                        {notification.message}
                      </p>
                    </div>
                    {!notification.read && (
                      <div className="h-2 w-2 rounded-full bg-volexx-blue-500 flex-shrink-0 mt-2"></div>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Notification Settings */}
        <div className="mt-8 bg-white rounded-volexx-lg shadow-volexx-md p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Notification Preferences</h2>
          <div className="space-y-3">
            <label className="flex items-center justify-between">
              <span className="text-sm font-semibold text-gray-700">Order Status Updates</span>
              <input type="checkbox" defaultChecked className="toggle" />
            </label>
            <label className="flex items-center justify-between">
              <span className="text-sm font-semibold text-gray-700">OTP Delivery Alerts</span>
              <input type="checkbox" defaultChecked className="toggle" />
            </label>
            <label className="flex items-center justify-between">
              <span className="text-sm font-semibold text-gray-700">Wallet Confirmations</span>
              <input type="checkbox" defaultChecked className="toggle" />
            </label>
            <label className="flex items-center justify-between">
              <span className="text-sm font-semibold text-gray-700">Admin Announcements</span>
              <input type="checkbox" defaultChecked className="toggle" />
            </label>
            <label className="flex items-center justify-between">
              <span className="text-sm font-semibold text-gray-700">Email Notifications</span>
              <input type="checkbox" className="toggle" />
            </label>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Notifications;
